import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, ShoppingCart, Loader2, AlertTriangle, PackageOpen, ChevronRight, LayoutList, Rows3 } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 });
const dt = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const imgSrc = (path) => `${API_BASE}${path}&auth=${localStorage.getItem("cop31_token") || ""}`;

const GroupedProducts = ({ orders }) => {
  const [open, setOpen] = useState({});
  const groups = useMemo(() => {
    const cats = new Map();
    orders.forEach((o) => (o.lines || []).forEach((ln) => {
      const cat = (ln.category || "Diğer").trim() || "Diğer";
      if (!cats.has(cat)) cats.set(cat, new Map());
      const prods = cats.get(cat);
      const key = ln.sku || `${ln.name}|${ln.variant || ""}`;
      if (!prods.has(key)) prods.set(key, { name: ln.name, variant: ln.variant, sku: ln.sku, category: ln.category, image: ln.image, unit: ln.unit, currency: ln.currency || o.currency, qty: 0, total: 0, refs: new Set() });
      const p = prods.get(key);
      p.qty += Number(ln.qty || 0);
      p.total += Number(ln.lineTotal || 0);
      p.refs.add(o.ref);
      if (!p.image && ln.image) p.image = ln.image;
    }));
    return [...cats.entries()]
      .map(([cat, prods]) => {
        const items = [...prods.values()].sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr"));
        return { cat, items, qty: items.reduce((s, p) => s + p.qty, 0), total: items.reduce((s, p) => s + p.total, 0), currency: items[0]?.currency };
      })
      .sort((a, b) => a.cat.localeCompare(b.cat, "tr"));
  }, [orders]);

  if (groups.length === 0) {
    return (
      <div className="bg-white border border-slate-200 px-6 py-14 text-center text-slate-400" data-testid="orders-grouped-empty">
        <PackageOpen size={28} className="mx-auto mb-2 text-slate-300" /> Görüntülenecek ürün yok.
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200" data-testid="orders-grouped">
      <div className="px-5 py-3 border-b border-slate-200 grid grid-cols-[1fr_120px_110px_130px] gap-3 text-xs font-bold text-slate-600">
        <div>Ürün</div>
        <div className="text-center">Toplam Miktar</div>
        <div className="text-center">Sipariş Sayısı</div>
        <div className="text-right">Toplam Tutar</div>
      </div>
      {groups.map((g) => {
        const isOpen = !!open[g.cat];
        return (
          <div key={g.cat} className="border-b border-slate-100 last:border-b-0">
            <button
              type="button"
              onClick={() => setOpen((s) => ({ ...s, [g.cat]: !s[g.cat] }))}
              data-testid={`orders-group-${g.cat.replace(/\s+/g, "-").toLowerCase()}`}
              className="w-full px-5 py-3.5 flex items-center gap-2 bg-slate-50/70 hover:bg-slate-100 text-left transition-colors duration-150"
            >
              <ChevronRight size={15} className={`text-slate-500 shrink-0 transition-transform duration-150 ${isOpen ? "rotate-90" : ""}`} />
              <span className="font-heading text-sm font-extrabold tracking-wider uppercase text-emerald-900">{g.cat}</span>
              <span className="text-xs text-slate-500">{g.items.length} ürün · {fmt(g.qty)} adet</span>
              <span className="ml-auto text-sm font-bold tabular-nums">{fmt(g.total)} {g.currency === "TRY" ? "TL" : g.currency}</span>
            </button>
            {isOpen && g.items.map((p, i) => (
              <div key={i} className="px-5 py-3 grid grid-cols-[1fr_120px_110px_130px] gap-3 items-center border-t border-slate-50" data-testid={`orders-group-item-${g.cat.replace(/\s+/g, "-").toLowerCase()}-${i}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {p.image ? (
                    <img src={imgSrc(p.image)} alt={p.name} loading="lazy" className="w-11 h-11 object-cover bg-slate-100 border border-slate-200 shrink-0" />
                  ) : (
                    <div className="w-11 h-11 bg-slate-50 border border-slate-200 shrink-0 flex items-center justify-center text-slate-300"><PackageOpen size={14} /></div>
                  )}
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-slate-800 truncate">
                      {p.name}
                      {p.variant && <span className="ml-1.5 text-[10px] font-bold border border-slate-200 bg-slate-50 text-slate-600 px-1.5 py-0.5 align-middle">{p.variant}</span>}
                    </div>
                    <div className="text-[10px] text-slate-400 truncate">{p.sku}{p.category ? ` · ${p.category}` : ""}</div>
                  </div>
                </div>
                <div className="text-center"><span className="inline-block text-xs font-bold tabular-nums bg-slate-100 border border-slate-200 px-2.5 py-1">{fmt(p.qty)} {p.unit || "adet"}</span></div>
                <div className="text-center text-sm tabular-nums">{p.refs.size}</div>
                <div className="text-right text-sm font-bold tabular-nums">{fmt(p.total)} {p.currency === "TRY" ? "TL" : p.currency}</div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default function OrdersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [unmapped, setUnmapped] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [filterProject, setFilterProject] = useState("");
  const isAssigner = user?.role === "assigner";
  const [view, setView] = useState(user?.role === "assigner" ? "grouped" : "list");
  const backPath = user?.role === "uretici" ? "/uretici" : user?.role === "superadmin" ? "/admin/ayarlar" : "/atayici";

  useEffect(() => {
    api.get("/ratecard/orders")
      .then((r) => { setOrders(r.data.orders || []); setUnmapped(r.data.unmapped); setLoaded(true); })
      .catch((e) => { setLoadError(apiError(e)); toast.error(apiError(e)); setLoaded(true); });
  }, []);

  const projects = useMemo(() => {
    const m = new Map();
    orders.forEach((o) => { if (o.project && !m.has(o.project.id)) m.set(o.project.id, o.project); });
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name, "tr"));
  }, [orders]);

  const filtered = useMemo(() => orders.filter((o) => !filterProject || o.project_id === filterProject), [orders, filterProject]);

  const totals = useMemo(() => {
    const t = {};
    filtered.forEach((o) => { const c = o.currency || "TRY"; t[c] = (t[c] || 0) + Number(o.total || 0); });
    return Object.entries(t).map(([c, v]) => `${fmt(v)} ${c === "TRY" ? "TL" : c}`).join(" · ") || "0";
  }, [filtered]);

  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="orders-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-heading text-xl font-extrabold tracking-tight">Gett<span className="text-slate-400">Proposal</span></div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold flex items-center gap-1"><ShoppingCart size={10} /> Siparişler · ratecard31</div>
          </div>
          <button onClick={() => navigate(backPath)} data-testid="orders-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-900 px-3 py-2 transition-colors duration-150">
            <ArrowLeft size={14} /> Geri Dön
          </button>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight">Siparişler</h1>
            <p className="text-sm text-slate-500 mt-1">{loaded ? `${filtered.length} sipariş · Toplam: ${totals}` : "Yükleniyor..."}</p>
          </div>
          <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} data-testid="orders-filter-project" className="h-10 border border-slate-200 px-3 text-sm bg-white sm:w-[320px]">
            <option value="">Tüm projeler ({orders.length})</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}{p.stand_no ? ` — ${p.stand_no}` : ""}</option>)}
          </select>
        </div>
        {isAssigner && (
          <div className="flex border border-slate-200 w-fit bg-white">
            <button type="button" onClick={() => setView("grouped")} data-testid="orders-view-grouped" className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold border-b-2 transition-colors duration-150 ${view === "grouped" ? "text-slate-900 border-slate-900" : "border-transparent text-slate-400 hover:text-slate-700"}`}>
              <Rows3 size={13} /> Ürün Grupları
            </button>
            <button type="button" onClick={() => setView("list")} data-testid="orders-view-list" className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold border-b-2 transition-colors duration-150 ${view === "list" ? "text-slate-900 border-slate-900" : "border-transparent text-slate-400 hover:text-slate-700"}`}>
              <LayoutList size={13} /> Sipariş Listesi
            </button>
          </div>
        )}
        {loadError && (
          <div className="w-full flex items-center gap-2 text-xs font-semibold bg-red-50 text-red-700 border border-red-200 px-4 py-3 mb-4" data-testid="orders-load-error">
            <AlertTriangle size={14} /> Siparişler alınamadı: {loadError}
          </div>
        )}
        {user?.role === "superadmin" && unmapped > 0 && (
          <button type="button" onClick={() => navigate("/admin/ayarlar")} data-testid="orders-unmapped-warning" className="w-full flex items-center gap-2 text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200 px-4 py-3 hover:bg-amber-100 transition-colors duration-150">
            <AlertTriangle size={14} /> {unmapped} sipariş henüz bir projeyle eşleştirilmedi — Ayarlar &gt; Sipariş Eşleştirme bölümünden eşleştirin
          </button>
        )}
        {!loaded && <div className="flex justify-center py-16 text-slate-400"><Loader2 size={22} className="animate-spin" /></div>}
        {loaded && filtered.length === 0 && (
          <div className="bg-white border border-slate-200 px-6 py-14 text-center text-slate-400" data-testid="orders-empty">
            <PackageOpen size={28} className="mx-auto mb-2 text-slate-300" /> Görüntülenecek sipariş yok.
          </div>
        )}
        {loaded && filtered.length > 0 && isAssigner && view === "grouped" && <GroupedProducts orders={filtered} />}
        {(!isAssigner || view === "list") && filtered.map((o) => (
          <div key={o.ref} className="bg-white border border-slate-200" data-testid={`order-card-${o.ref}`}>
            <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="font-heading text-base font-extrabold">{o.project?.name}{o.project?.stand_no ? ` — ${o.project.stand_no}` : ""}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">
                  #{o.ref} · {dt(o.placedAt)} · ratecard: {o.organisation}
                  {o.source === "manual" && <span className="ml-1 text-amber-600 font-semibold">· manuel</span>}
                  {o.status ? ` · ${o.status}` : ""}
                </div>
              </div>
              <div className="text-right">
                <div className="font-heading text-lg font-extrabold tabular-nums">{fmt(o.total)} {o.currency === "TRY" ? "TL" : o.currency}</div>
                <div className="text-[10px] text-slate-400 tabular-nums">Ara toplam {fmt(o.subtotal)} · KDV {fmt(o.vatAmount)}</div>
              </div>
            </div>
            <div className="divide-y divide-slate-50">
              {(o.lines || []).map((ln, i) => (
                <div key={i} className="px-5 py-2.5 flex items-center gap-3 text-sm" data-testid={`order-line-${o.ref}-${i}`}>
                  {ln.image ? (
                    <img src={imgSrc(ln.image)} alt={ln.name} loading="lazy" className="w-10 h-10 object-cover bg-slate-100 border border-slate-200 shrink-0" />
                  ) : (
                    <div className="w-10 h-10 bg-slate-50 border border-slate-200 shrink-0 flex items-center justify-center text-slate-300"><PackageOpen size={14} /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-800 truncate">{ln.name}{ln.variant ? <span className="text-slate-400 font-normal"> · {ln.variant}</span> : null}</div>
                    <div className="text-[10px] text-slate-400">{ln.sku}{ln.category ? ` · ${ln.category}` : ""}</div>
                  </div>
                  <div className="text-xs text-slate-500 tabular-nums whitespace-nowrap">{fmt(ln.qty)} {ln.unit || "adet"} × {fmt(ln.unitPrice)}</div>
                  <div className="text-sm font-bold tabular-nums whitespace-nowrap w-[110px] text-right">{fmt(ln.lineTotal)} {ln.currency === "TRY" ? "TL" : ln.currency}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </main>
    </div>
  );
}
